/**
 * Session Assistant — shared bits for its screens.
 *
 * The option lists mirror apps/api/src/lib/booking-compose.js (the server
 * validates against its own copy; keep the two in step). The display
 * helpers follow the Session Log's: 12-hour clocks, "Oct 9" days.
 */
import { fmtClock } from '../../lib/date.js'

// Renamed from "Session Assistant" (Charles, 2026-10-04) — display only; routes, tables and files keep the old name.
export const APP_NAME = 'Session Builder'
/**
 * How a session opens from the calendar (Charles, 2026-10-10, after v3's
 * detail screen): 'tiles' — a tap on ANY session opens the full detail
 * (v3's header card that unfolds to the studio and engineer choices, and
 * the tiles under it), and the small card is only for a slot being booked,
 * as a sheet up from the bottom of a phone; 'classic' — a tap opens the
 * small anchored card, as before 2026-10-10, with the booking page behind
 * "All details". One word to go back if the new way doesn't suit.
 */
export const SESSION_UI = 'tiles'
export const APP_DESC = 'Book a session — it lands on the engineer’s calendar'

export const ROOMS = ['Studio A', 'Studio B', 'Studio C', 'Studio 1', 'Studio 5']
export const SESSION_TYPES = ['Audiobook', 'Podcast', 'ADR', 'Commercial VO', 'Animation', 'Pickups', 'Other']
/**
 * The word a session's type puts in front of its name on the calendar, as
 * the server's composeTitle writes it: "Pickups - " (2026-10-05) and
 * "ADR - " (Charles, 2026-10-14) — never twice when the name already has it.
 */
export function typeLead(sessionType, name = '') {
  const t = String(sessionType || '').trim().toLowerCase()
  if (t === 'pickups') return 'Pickups - '
  if (t === 'adr' && !/^adr\b/i.test(String(name).trim())) return 'ADR - '
  return ''
}
// Several can be ticked (Charles, 2026-10-05); the booking carries them comma-joined, "Zoom, WebEx".
// "Other" last: a one-off typed on the booking, or made permanent (the server's list then carries it).
export const REMOTE_METHODS = ['In studio', 'Zoom', 'WebEx', 'Riverside', 'Session Link', 'ipDTL', 'Source Connect', 'Comrex', 'Phone patch', 'Other']
// The methods with a link of their own (a link field each); Zoom's link has its own handling.
export const LINKED_METHODS = ['WebEx', 'Riverside', 'Session Link', 'ipDTL']
/** "Zoom, WebEx" → ['Zoom', 'WebEx'] in list order (`list` = the picker's list, custom names included). Mirrors splitMethods in booking-compose.js. */
export function splitMethods(value, list = REMOTE_METHODS) {
  const wanted = new Set((Array.isArray(value) ? value : String(value || '').split(/\s*,\s*/)).map((p) => String(p || '').trim().toLowerCase()))
  const found = list.filter((m) => wanted.has(m.toLowerCase()))
  return found.length > 1 ? found.filter((m) => m !== 'In studio') : found
}
export const joinMethods = (value, list = REMOTE_METHODS) => splitMethods(value, list).join(', ')
export const SAMPLE_RATES = ['48k', '44.1k']
export const BIT_DEPTHS = ['16 bit', '24 bit']
export const STATUS_OPTIONS = ['Confirmed', 'Hold']
export const VIDEO_CHOICES = [['pro', 'Pro video'], ['casual', 'iPad / phone / casual'], ['none', 'No camera']]
// Mirror CAMERA_COUNTS / MIC_COUNTS / MIC_CHOICES in booking-compose.js.
export const CAMERA_COUNTS = ['1', '2', '3', '4']
export const MIC_COUNTS = ['1', '2', '3', '4', '5', '6']
export const MIC_CHOICES = ['416', 'SM7', 'RE20', 'U87 (black)', 'U87 (silver)', 'U87 (western)', 'Mojave (black)', 'Mojave (silver)', 'MKH50', 'CMIT5', 'V4U', 'COS-11D', 'Countryman B3', 'DPA']

// Google Calendar's own colour picker, in its order — mirrors apps/api/src/lib/google-colors.js.
export const GOOGLE_COLORS = [
  ['Cocoa', '#795548'], ['Flamingo', '#E67C73'], ['Tomato', '#D50000'], ['Tangerine', '#F4511E'], ['Pumpkin', '#EF6C00'], ['Mango', '#F09300'],
  ['Eucalyptus', '#009688'], ['Basil', '#0B8043'], ['Pistachio', '#7CB342'], ['Avocado', '#C0CA33'], ['Citron', '#E4C441'], ['Banana', '#F6BF26'],
  ['Sage', '#33B679'], ['Peacock', '#039BE5'], ['Cobalt', '#4285F4'], ['Blueberry', '#3F51B5'], ['Lavender', '#7986CB'], ['Wisteria', '#B39DDB'],
  ['Graphite', '#616161'], ['Birch', '#A79B8E'], ['Radicchio', '#AD1457'], ['Cherry Blossom', '#D81B60'], ['Grape', '#8E24AA'], ['Amethyst', '#9E69AF'],
]

export const inputCls =
  'tap w-full border border-hairline bg-grouped px-3 py-2.5 text-[16px] placeholder:text-faint focus:bg-white focus:outline-none focus:ring-2 focus:ring-link'

/** 'YYYY-MM-DD' → 'Thu, Oct 9' (today / tomorrow named). */
export function fmtDayLong(d, { today = todayISO() } = {}) {
  if (!d) return ''
  if (d === today) return 'Today'
  if (d === addDays(today, 1)) return 'Tomorrow'
  const [y, m, day] = d.split('-').map(Number)
  const dt = new Date(y, m - 1, day)
  return Number.isNaN(dt.getTime()) ? d : dt.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })
}

export function fmtDayFull(d) {
  if (!d) return ''
  const [y, m, day] = d.split('-').map(Number)
  const dt = new Date(y, m - 1, day)
  return Number.isNaN(dt.getTime()) ? d : dt.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
}

/** 'YYYY-MM-DD' → 'Oct 9'. */
export function fmtDayShort(d) {
  if (!d) return ''
  const [y, m, day] = d.split('-').map(Number)
  const dt = new Date(y, m - 1, day)
  return Number.isNaN(dt.getTime()) ? d : dt.toLocaleDateString([], { month: 'short', day: 'numeric' })
}

export const fmtRange = (a, b) => `${fmtClock(a)}–${fmtClock(b)}`
/** "Thu Oct 9" — the day as v3's hold menu names it. */
export function fmtDayWeek(d) {
  const [y, m, day] = String(d || '').split('-').map(Number)
  const dt = new Date(y, m - 1, day)
  return Number.isNaN(dt.getTime()) ? String(d || '') : dt.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' }).replace(',', '')
}
/** "1:30 – 3:30 PM", "9 AM – 12 PM" — AM/PM once when it doesn't change (v3's fullRange). */
export function fullRange(start, end) {
  const a = fmtClock(start)
  const b = fmtClock(end)
  const ap = (x) => x.slice(-2)
  return ap(a) === ap(b) ? `${a.slice(0, -3)} – ${b}` : `${a} – ${b}`
}

/** "812 3456 7890" from a Zoom link — mirrors zoomMeetingId in apps/api/src/lib/booking-compose.js; keep in step. */
export function zoomMeetingId(link) {
  const m = /zoom\.us\/(?:j|w|s|wc(?:\/join)?)\/(\d{9,11})\b/i.exec(String(link || ''))
  if (!m) return ''
  const d = m[1]
  const cut = d.length === 11 ? 7 : 6
  return `${d.slice(0, 3)} ${d.slice(3, cut)} ${d.slice(cut)}`
}

/**
 * Plain-words notes for engineers the calendars mark as off — "No Cathleen"
 * in the all-day strip, "No Katherine after 3" as a timed entry (Charles,
 * 2026-10-06). `awayByDay` is each day's `away` list from
 * /assistant/clashes, `days` the days it was asked for; `onlyIds` narrows it
 * to some engineers. One sentence each:
 *   "Cathleen is marked off that day (“No cathleen” on the calendar)."
 *   "Katherine is marked off 3–8 PM on Tue, Oct 13 (“No Katherine after 3” …)."
 */
export function awayNotes(awayByDay, days, onlyIds = null) {
  const multi = days.length > 1
  const onDay = (d) => {
    const w = fmtDayLong(d)
    return w === 'Today' || w === 'Tomorrow' ? w.toLowerCase() : `on ${w}`
  }
  const people = new Map()
  ;(awayByDay || []).forEach((rows, i) => {
    for (const r of rows || []) {
      if (onlyIds && !onlyIds.has(r.calendarId)) continue
      const p = people.get(r.calendarId) || { name: r.name, titles: [], dates: [], times: [] }
      const date = days[i]?.date
      if (r.allDay) {
        if (!p.dates.includes(date)) p.dates.push(date)
      } else {
        const when = `${fmtRange(r.start, r.end)}${multi ? ` ${onDay(date)}` : ''}`
        if (!p.times.includes(when)) p.times.push(when)
      }
      if (!p.titles.includes(r.title)) p.titles.push(r.title)
      people.set(r.calendarId, p)
    }
  })
  const and = (xs) => (xs.length <= 1 ? xs[0] || '' : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`)
  // "on Tue, Oct 20 and Wed, Oct 21" — one "on" for the whole days.
  const dayList = (ds) => {
    if (!multi) return 'that day'
    const words = ds.map((d) => onDay(d))
    return words.every((w) => w.startsWith('on ')) ? `on ${and(words.map((w) => w.slice(3)))}` : and(words)
  }
  return [...people.values()].map((p) => {
    const pieces = [...(p.dates.length ? [dayList(p.dates)] : []), ...p.times]
    return `${p.name} is marked off ${and(pieces)} (“${p.titles[0]}” on the calendar).`
  })
}

/** "Oct 9" / "Oct 9–13 · 5 days" for a booking's run. */
export function fmtRun(days) {
  if (!days?.length) return ''
  if (days.length === 1) return fmtDayFull(days[0].date)
  return `${fmtDayShort(days[0].date)}–${fmtDayShort(days[days.length - 1].date)} · ${days.length} days`
}

export function todayISO() {
  const t = new Date()
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`
}

export function addDays(iso, n) {
  const [y, m, d] = iso.split('-').map(Number)
  const dt = new Date(y, m - 1, d + n)
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`
}

/** The nearest quarter hour at or after 'HH:MM' — the grid the time picker offers. */
export function toQuarter(hhmm) {
  if (!/^\d{2}:\d{2}$/.test(hhmm || '')) return hhmm || ''
  const [h, m] = hhmm.split(':').map(Number)
  const total = Math.min(23 * 60 + 45, Math.ceil((h * 60 + m) / 15) * 15)
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}

/** 'HH:MM' + hours → 'HH:MM', clamped to the day. */
export function addHours(hhmm, hours) {
  const [h, m] = hhmm.split(':').map(Number)
  const total = Math.min(23 * 60 + 59, h * 60 + m + Math.round(hours * 60))
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}

/** "Studio A" → "(A)", "Studio 1" → "(1)" — the marker the list leads with (Charles, 2026-10-04). */
/**
 * "PRHA - The Inheritance" with the booking's extra words for the engineer
 * in front or after — "Rush - PRHA - The Inheritance" — the way the
 * calendar title carries them (mirrors titleBody in
 * apps/api/src/lib/booking-compose.js; keep in step).
 */
export function nameWithExtra(it) {
  const name = [it.clientName, it.project].map((x) => String(x || '').trim()).filter(Boolean).join(' - ')
  const x = String(it.titleExtra || '').replace(/\s+/g, ' ').trim()
  if (!x) return name
  if (!name) return x
  return it.titleExtraPlace === 'after' ? `${name} - ${x}` : `${x} - ${name}`
}

export function roomMarker(room) {
  const code = /Studio\s+([15ABC])/i.exec(room || '')?.[1]?.toUpperCase()
  return code ? `(${code})` : ''
}

/**
 * Ink or white, whichever reads on a solid block of `hex` — the day view
 * fills blocks with the engineer's colour outright (Charles, 2026-10-04,
 * with a Google Calendar screenshot: "more opaque like this"), so yellow
 * and cyan take dark text while navy and brown take white.
 */
export function textOn(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim())
  if (!m) return '#FFFFFF'
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(m[1].slice(i, i + 2), 16) / 255)
  const lin = (c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4)
  const lum = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b)
  return lum > 0.4 ? '#0F0F0E' : '#FFFFFF'
}

/**
 * A colour faded toward white, the way Google shows a session that has
 * already happened (Charles, 2026-10-08: "make the events dim the way
 * google does"). `t` is how far toward white: 0 keeps it, 1 is white.
 */
export function fadeColor(hex, t = 0.55) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim())
  if (!m) return hex
  const mix = (i) => Math.round(parseInt(m[1].slice(i, i + 2), 16) * (1 - t) + 255 * t)
  return `#${[0, 2, 4].map((i) => mix(i).toString(16).padStart(2, '0')).join('')}`.toUpperCase()
}

/** Minutes past midnight now, kept current — for fading what has finished. */
export function minutesNow() {
  const d = new Date()
  return d.getHours() * 60 + d.getMinutes()
}

/**
 * The Sunday that starts the week this day is in — the week view runs
 * Sunday to Saturday (Charles, 2026-10-07; Mon–Sun from 2026-10-05).
 */
export function sundayOf(iso) {
  const [y, m, d] = iso.split('-').map(Number)
  return addDays(iso, -new Date(y, m - 1, d).getDay())
}

/**
 * The room's colour token, as a class — same palette as the CDM Calendar.
 * Spelled out in full: Tailwind only makes the classes it can read in the
 * source, and a `bg-room-${code}` built at run time was never made, so the
 * dots came out see-through (found 2026-10-08).
 */
const ROOM_DOT = { A: 'bg-room-a', B: 'bg-room-b', C: 'bg-room-c', 1: 'bg-room-1', 5: 'bg-room-5' }
export function roomDotClass(room) {
  const code = /Studio\s+([15ABC])/i.exec(room || '')?.[1]?.toUpperCase()
  return (code && ROOM_DOT[code]) || 'bg-faint'
}

/** One quiet phrase for where the booking stands with Google. */
export function calendarStateLabel(state) {
  return {
    published: 'On the calendar',
    pending: 'Not on the calendar yet',
    'no-engineer': 'No engineer picked — not on any calendar',
    nowhere: 'No calendar for this engineer yet',
    removing: 'Being removed',
    cancelled: 'Cancelled',
    changed: 'Changed in Google — waiting for you to choose',
  }[state] || ''
}

/** What was changed in Google on a session (list rows carry `googleChanged`: the kinds), in words. */
export function googleChangeWords(kinds = []) {
  const w = { wording: 'renamed or notes changed', times: 'moved to another time', calendar: 'moved to another engineer', deleted: 'deleted' }
  const out = kinds.map((k) => w[k]).filter(Boolean).join(', ')
  return out ? out[0].toUpperCase() + out.slice(1) : ''
}

// engineerCalendarId: this day's own engineer; null = the booking's (Charles, 2026-10-05).
export const emptyDay = (date = todayISO()) => ({ date, startTime: '', endTime: '', engineerCalendarId: null })

/** Field → words, for "apply these to the other days?" (Charles, 2026-10-05). */
export const FIELD_WORDS = {
  status: 'status', clientName: 'client', project: 'project', titleExtra: 'extra in the title', titleExtraPlace: 'extra in the title', sessionType: 'session type', sessionTypeOther: 'session type',
  videoOption: 'video', videoDetails: 'video details', cameraCount: 'cameras', micCount: 'mic count', micPreference: 'mics', room: 'room',
  remoteMethod: 'remote setup', zoomOwner: 'whose Zoom', zoomAccountId: 'Zoom account', remoteLink: 'remote link', remoteLinks: 'connection links', remoteOther: 'other connection', narrators: 'narrator',
  author: 'author', producer: 'producer', director: 'director', contactName: 'contact', contactEmail: 'contact email', contactPhone: 'contact phone',
  scriptLinks: 'script links', scriptFiles: 'scripts', pickupPackLinks: 'pickup pack links', pickupPackFiles: 'pickup pack', downloadLinks: 'downloads', editGoesTo: 'edit goes to', sampleRate: 'sample rate', bitDepth: 'bit depth', notes: 'notes',
}

/** The next working day after `iso` (a Friday → Monday). */
export function nextWorkingDay(iso) {
  let d = addDays(iso, 1)
  const dow = new Date(d + 'T12:00').getDay()
  if (dow === 6) d = addDays(d, 2)
  if (dow === 0) d = addDays(d, 1)
  return d
}

/**
 * The office's copy of every Zoom link emailed to a client (Charles,
 * 2026-10-13) — ticked by default, untickable. Mirrors ZOOM_EMAIL_CC in
 * apps/api/src/services/zoom-meetings.js; keep the two in step.
 */
export const OFFICE_CC = 'office@cdmstudios.com'

/** "Zoom link emailed to a@x.com, copy to office@… and Katherine" — for the toasts after a send. */
export const emailedWords = (z) => {
  const copies = [z?.cc, ...(z?.engineers || [])].filter(Boolean)
  const none = z?.noEmail?.length ? ` — no address for ${andList(z.noEmail)}, so no copy to them` : ''
  return `Zoom link emailed to ${z?.to || 'the client'}${copies.length ? `, copy to ${andList(copies)}` : ''}${none}`
}
const andList = (xs) => (xs.length < 2 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`)

/**
 * The engineers who get a copy of the Zoom link email — every engineer on
 * the session's days, once each, at their account's address, as a Cc the
 * client sees (Charles, 2026-11-04). Works on a form or a loaded booking
 * (both carry engineerCalendarId and days[].engineerCalendarId). Mirrors
 * engineerCopiesFor in apps/api/src/services/zoom-meetings.js.
 * [{ name, email }] — email '' when no account matches (Other).
 */
export function engineerCopies(form, calendars = []) {
  const ids = new Set()
  for (const d of form?.days || []) {
    const id = d.engineerCalendarId || form.engineerCalendarId
    if (id) ids.add(id)
  }
  if (ids.size === 0 && form?.engineerCalendarId) ids.add(form.engineerCalendarId)
  return [...ids]
    .map((id) => calendars.find((c) => c.id === id))
    .filter((c) => c && !isBucket(c))
    .map((c) => ({ name: String(c.name || '').trim().split(/\s+/)[0], email: c.email || '' }))
}

/** "Katherine (k@…) gets a copy too." */
export function engineerCopyWords(list = []) {
  const got = list.filter((e) => e.email)
  const none = list.filter((e) => !e.email)
  const parts = []
  if (got.length) parts.push(`${andList(got.map((e) => `${e.name} (${e.email})`))} ${got.length > 1 ? 'get' : 'gets'} a copy too.`)
  if (none.length) parts.push(`No email address for ${andList(none.map((e) => e.name))}, so no copy goes to them.`)
  return parts.join(' ')
}

export const emptyBooking = () => ({
  status: 'confirmed',
  runId: null, // "Book it and add a day": the run this booking belongs to
  clientId: null,
  clientName: '',
  project: '',
  titleExtra: '', // extra words for the engineer in the calendar title (Charles, 2026-10-08)
  titleExtraPlace: 'before', // …before or after the client and project
  sessionType: '',
  sessionTypeOther: '',
  videoOption: '',
  videoDetails: '',
  cameraCount: '',
  micCount: '',
  micPreference: '',
  room: '',
  days: [emptyDay()],
  engineerCalendarId: null,
  remoteMethod: '',
  zoomOwner: '',
  zoomAccountId: null,
  emailZoomLink: false,
  zoomEmailCc: true, // …with a copy to the office (2026-10-13); untick to leave it off
  remoteLink: '', // the Zoom link on "their link"
  remoteLinks: {}, // the other methods' links by name: { WebEx: 'https://…' }
  remoteOther: '', // what "Other" means on this booking
  narrators: '',
  author: '',
  producer: '',
  director: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  scriptLinks: '',
  scriptFiles: [],
  pickupPackLinks: '', // the pickup pack: pasted links, one per line (Charles, 2026-10-05)
  pickupPackFiles: [], // …or uploaded to Dropbox Pickup Packs/<client - project>/
  downloadLinks: '', // links to files to download before the session, one per line (Charles, 2026-10-22)
  editGoesTo: '',
  sampleRate: '',
  bitDepth: '',
  notes: '',
})

/**
 * A Google-typed booking's prefill (GET /assistant/events/:uid) → the
 * form's state. Each day keeps the `googleEvents` it already is on the
 * calendars, which is what tells the server to rewrite those events in
 * place rather than add new ones.
 */
export function fromPrefill(p) {
  const f = emptyBooking()
  for (const k of Object.keys(f)) if (k !== 'days' && p[k] !== undefined && p[k] !== null) f[k] = p[k]
  f.clientId = p.clientId ?? null
  f.engineerCalendarId = p.engineerCalendarId ?? null
  f.zoomAccountId = p.zoomAccountId ?? null
  f.days = (p.days || []).map((d) => ({ date: d.date, startTime: d.startTime, endTime: d.endTime, googleEvents: d.googleEvents || [], engineerCalendarId: d.engineerCalendarId ?? null }))
  if (!f.days.length) f.days = [emptyDay()]
  return f
}

/** A booking from the API → the form's state (drops the read-only fields). */
export function toForm(b) {
  const f = emptyBooking()
  for (const k of Object.keys(f)) if (k !== 'days' && b[k] !== undefined && b[k] !== null) f[k] = b[k]
  f.clientId = b.clientId ?? null
  f.engineerCalendarId = b.engineerCalendarId ?? null
  f.zoomAccountId = b.zoomAccountId ?? null
  f.runId = b.runId ?? null
  f.days = (b.days || []).map((d) => ({ id: d.id, date: d.date, startTime: d.startTime, endTime: d.endTime, engineerCalendarId: d.engineerCalendarId ?? null, room: d.room ?? null }))
  if (!f.days.length) f.days = [emptyDay()]
  return f
}

/**
 * A day's own studio (Charles, 2026-11-04: change the studio of one day of
 * a multi-day booking). `room` on a day is its own pick, null = the
 * session's. Put back the way the server stores it: when every day ends up
 * in one studio, that is the session's and no day has its own; otherwise
 * the session keeps its studio (or takes the first day's, when it had
 * none) and only the days in another one carry it.
 */
export const dayRoomOf = (form, d) => d?.room || form?.room || ''
export function settleDayRooms(room, days) {
  const eff = days.map((d) => d.room || room || '')
  if (eff.every((e) => e === eff[0])) return { room: eff[0], days: days.map((d) => ({ ...d, room: null })) }
  const base = room || eff.find(Boolean) || ''
  return { room: base, days: days.map((d, i) => ({ ...d, room: eff[i] && eff[i] !== base ? eff[i] : null })) }
}

/**
 * Rebook (Charles, 2026-10-11: "an option when clicking cancel session to
 * rebook it, filling in all the same details and asking for a new date and
 * time"): a new session's detail holding every field of booking `b`, with
 * no day yet — the detail asks for the new one. `dayIds` are the days being
 * rebooked (all of them when empty). When those days all had one engineer,
 * the new session is theirs; a mix keeps each day's in `was`, in order.
 */
export function rebookSeed(b, dayIds = []) {
  const ids = new Set(Array.isArray(dayIds) && dayIds.length ? dayIds : (b.days || []).map((d) => d.id))
  const was = (b.days || []).filter((d) => ids.has(d.id)).map((d) => ({ id: d.id, date: d.date, startTime: d.startTime, endTime: d.endTime, engineerCalendarId: d.engineerCalendarId ?? null }))
  const form = toForm(b)
  form.days = []
  form.emailZoomLink = false
  const own = [...new Set(was.map((d) => d.engineerCalendarId ?? b.engineerCalendarId ?? null))]
  if (own.length === 1) {
    form.engineerCalendarId = own[0]
    for (const d of was) d.engineerCalendarId = null
  }
  return {
    form,
    rebook: {
      bookingId: b.id,
      dayIds: was.map((d) => d.id),
      was,
      whole: was.length === (b.days || []).length,
      lateCancelled: Boolean(b.lateCancel),
      zoomMeeting: Boolean(b.zoomMeeting),
      name: nameWithExtra(b) || 'this session',
    },
  }
}

/**
 * The leading words of a calendar entry's title — "call with" of "call
 * with K. Daniels" — the phrase an "ignore from now on" rule would use
 * (mirrors ignorePhraseOf in apps/api/src/lib/tidy-learning.js).
 */
export function ignorePhraseOf(summary) {
  const words = String(summary || '').toLowerCase().replace(/[’']/g, '').replace(/[^a-z0-9&]+/g, ' ').trim().split(' ').filter(Boolean)
  if (!words.length || /^\d/.test(words[0])) return null
  const take = words.length >= 2 && !/^\d/.test(words[1]) ? 2 : 1
  return words.slice(0, take).join(' ')
}

/**
 * Saving a session with no studio asks first (Charles, 2026-10-13: "it
 * should issue a warning before allowing me to do that"). Without a room
 * the calendar title has no "(A)" marker, so the engineers' CDM Calendar
 * board, the Session Log's suggestions and the log check never see it.
 * True = go ahead.
 */
export function okWithoutRoom(room) {
  if (room) return true
  return window.confirm('No studio is picked for this session.\n\nWithout one it won’t show on the CDM Calendar board, and the Session Log won’t suggest it.\n\nSave it without a studio?')
}

/**
 * Every session has a real engineer (Charles, 2026-10-27: "make sure that
 * unassigned is never picked. An engineer needs to be assigned. You can
 * remove that as a category"). Unassigned is never offered and never a
 * default, and a save with nobody picked stops and says so — the server
 * refuses it too.
 */
export const isBucket = (c) => /^unassigned$/i.test(String(c?.name || '').trim())
/** The engineers a session can go to: active calendars that are people. */
export const engineerChoices = (calendars = []) => calendars.filter((c) => c.isActive && !isBucket(c))
export const NEED_ENGINEER = 'Pick the engineer for this session first — every session needs one.'
/** A real engineer for the session — or for every day of it, when each day has its own. */
export function engineerPicked(engineerId, dayEngineerIds = [], calendars = []) {
  const ok = new Set(engineerChoices(calendars).map((c) => c.id))
  if (engineerId != null && ok.has(engineerId)) return true
  return dayEngineerIds.length > 0 && dayEngineerIds.every((id) => id != null && ok.has(id))
}
/** True = an engineer is picked; else says so, and the save stops. */
export function hasEngineer(engineerId, dayEngineerIds = [], calendars = []) {
  if (engineerPicked(engineerId, dayEngineerIds, calendars)) return true
  window.alert(NEED_ENGINEER)
  return false
}

/**
 * Contact addresses as pasted — "a@x.com b@y.com", "a@x.com; b@y.com" or a
 * Gmail To line ("Jane Doe <jane@x.com>, Bob <bob@y.com>") — tidied to
 * "jane@x.com, bob@y.com" (Charles, 2026-10-13). Text with no address in it
 * stays as typed.
 */
export function tidyEmails(text) {
  const raw = String(text || '').trim()
  const found = raw.match(/[^\s<>,;:()"'[\]]+@[^\s<>,;:()"'[\]]+\.[^\s<>,;:()"'[\]]+/g)
  if (!found) return raw
  const seen = new Set()
  return found
    .map((e) => e.replace(/[.]+$/, ''))
    .filter((e) => { const k = e.toLowerCase(); if (seen.has(k)) return false; seen.add(k); return true })
    .join(', ')
}
